import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { compressAvatar, dataUrlBytes } from '../utils/imageCompress';

// ── PUBLIC PROFILE CARDS ─────────────────────────────────────────────────────
//
// The smallest thing that lets one user see another's name and picture.
//
// WHY IT HAS TO EXIST. `users/{uid}` is readable by others only when the
// account is a DESIGNER — a customer's document is owner-only, by design,
// because it is not a public profile and nothing private may live on a
// readable one. So a customer who comments had no picture anyone else could
// look up, and showed the default icon to everybody.
//
// WHY NOT PUT THE PICTURE ON THE COMMENT. Two reasons, and the app has already
// been bitten by both: a full avatar is a data URL of up to 120KB, so copying
// it onto every comment makes reading a thread cost megabytes; and a copy goes
// stale the moment someone changes their photo, which is a bug this project
// has already had once, in chat.
//
// WHAT IT COSTS. One tiny document per user, read once per distinct author and
// then cached for the session. The avatar here is 64px — a couple of KB, not a
// couple of hundred — because it is only ever drawn at 30px in a comment row.

const CARD_MAX_PX = 64;
// Generous for a 64px image; the rules enforce the same ceiling.
const CARD_MAX_CHARS = 12000;

const ref = (uid) => doc(db, 'publicProfiles', uid);

// Per-session cache, including negative results: a user with no card must not
// be re-read for every comment they have written in a thread.
const cache = new Map();
const inflight = new Map();

/** One card, cached. Returns null when there is none. */
export async function fetchPublicProfile(uid) {
  if (!uid) return null;
  if (cache.has(uid)) return cache.get(uid);
  if (inflight.has(uid)) return inflight.get(uid);

  const p = getDoc(ref(uid))
    .then((snap) => {
      const v = snap.exists() ? snap.data() : null;
      cache.set(uid, v);
      return v;
    })
    .catch(() => { cache.set(uid, null); return null; })
    .finally(() => inflight.delete(uid));

  inflight.set(uid, p);
  return p;
}

/**
 * Writes (or refreshes) the caller's own card.
 *
 * Call it wherever the name or the avatar changes. Cheap and idempotent: one
 * small write, and the 64px rendition is derived from the avatar already in
 * memory rather than from the original file.
 *
 * Fire-and-forget by design — a card that failed to update is a stale picture
 * in other people's comment rows, not a failed profile save.
 */
export async function publishProfileCard(uid, { name = '', avatarUrl = '' } = {}) {
  if (!uid) return;
  try {
    let avatar = '';
    if (avatarUrl) {
      // Already small enough? Use it rather than re-encoding — an account with
      // no photo, or one that came from a URL, has nothing to shrink.
      avatar = avatarUrl.length <= CARD_MAX_CHARS
        ? avatarUrl
        : await shrink(avatarUrl);
    }
    await setDoc(ref(uid), {
      name: String(name || '').slice(0, 80),
      avatar,
      updatedAt: serverTimestamp(),
    });
    cache.delete(uid);
    return true;
  } catch (err) {
    // REPORTED, and reported as a failure. The caller records that the card is
    // published so it need not try again — if a rejected write looked like a
    // success, that record would be permanent and the card would never appear.
    console.warn('[publicProfile] not published:', err?.code || err);
    return false;
  }
}

/** Re-encodes a stored avatar down to the card size. */
async function shrink(dataUrl) {
  const blob = await (await fetch(dataUrl)).blob();
  const out = await compressAvatar(
    new File([blob], 'a', { type: blob.type || 'image/jpeg' }),
    CARD_MAX_PX,
  );
  const url = typeof out === 'string' ? out : out?.dataUrl || '';
  // If it still will not fit, no picture is better than a rejected write.
  return url && dataUrlBytes(url) * 4 / 3 <= CARD_MAX_CHARS ? url : '';
}
